import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Injector,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NgControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { ImageSelectorOptions } from './image-selector.interface';
import { uniqueId } from 'lodash-es';

const ACCEPT_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/x-png',
  'application/pdf'
];

@Component({
  selector: 'form-image-selector',
  templateUrl: './image-selector.component.html',
  styleUrls: ['./image-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageSelectorComponent),
      multi: true,
    },
  ],
})
export class ImageSelectorComponent
  implements ControlValueAccessor, OnInit, AfterViewInit
{
  public accept: string = ACCEPT_MIME_TYPES.join(',');
  @Input() public label: string;
  @Input() public options: ImageSelectorOptions;
  public imageUrl: string;
  public uniqueId: string;
  public preview: string | null;
  public formControl: UntypedFormControl;
  public maxFileSize: number;

  private dataUrlReader: FileReader = new FileReader();
  @ViewChild('inputFile') private inputFile: ElementRef<HTMLInputElement>;
  // tslint:disable-next-line:variable-name
  private _value: Blob | null;

  public onChange = (_: any) => {};
  public onTouched = () => {};

  public constructor(private injector: Injector) {
    this.uniqueId = uniqueId('image-selector');
  }

  get value(): Blob | null {
    return this._value;
  }
  set value(value: Blob | null) {
    this._value = value;
    this.onChange(this._value);
  }

  get pdfIconPreviewData() {
    return "data:image/webp;base64,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";
  }

  ngOnInit(): void {
    this.maxFileSize = this.options.maxFileSize * 1024 * 1024;

    this.dataUrlReader.onload = () => {
      // This event is thrown everytime we have successfully read a file.
      if(this.inputFile.nativeElement?.files && this.inputFile.nativeElement?.files[0]?.type === 'application/pdf') {
        this.preview = this.pdfIconPreviewData;
      } else {
        this.preview = this.dataUrlReader.result as string;
      }
    };
  }

  ngAfterViewInit(): void {
    const ngControl: NgControl | null = this.injector.get(NgControl, null);
    if (ngControl) {
      setTimeout(() => {
        this.formControl = ngControl.control as UntypedFormControl;
      });
    }
  }

  writeValue(imageUrl: string): void {
    if (imageUrl) {
      const request = new XMLHttpRequest();
      request.open('GET', imageUrl, true);
      request.responseType = 'blob';
      request.onload = () => {
        this.readFile(request.response);
      };
      request.send();
    } else {
      this.delete();
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public onFileChange(event: any): void {
    if (event.target.files && event.target.files.length) {
      const [file] = event.target.files;
      if (this.isBellowMaxFileSize(file)) {
        if (file.type && this.accept.includes(file.type)) {
          this.onTouched();
          this.readFile(file);
        } else {
          this.delete();
          this.formControl.setErrors({
            acceptedFileTypes: {
              types: this.accept,
            },
          });
        }
      } else {
        this.delete();
        this.formControl.setErrors({
          imageTooBig: {
            maxFileSize: this.maxFileSize,
          },
        });
      }
    }
  }

  public delete(): void {
    this.onTouched();
    this.value = null;
    this.preview = null;
    this.emptyInput();
  }

  private emptyInput(): void {
    if (this.inputFile && this.inputFile.nativeElement) {
      this.inputFile.nativeElement.value = '';
    }
  }

  private isBellowMaxFileSize(file: File): boolean {
    return file.size <= this.options.maxFileSize * 1024 * 1024;
  }

  private readFile(blob: Blob) {
    this.value = blob;
    this.dataUrlReader.readAsDataURL(blob);
  }
}
